package com.schoolconnect.app.datasource.network

import com.schoolconnect.app.datasource.APP_STORAGE_PREFIX
import com.schoolconnect.app.datasource.network.response.IdentityContextProfile
import com.schoolconnect.app.datasource.network.response.VerifyOtpResponse
import com.schoolconnect.app.datasource.network.response.resolveRoleCode
import com.russhwolf.settings.Settings
import kotlin.io.encoding.Base64
import kotlin.io.encoding.ExperimentalEncodingApi
import kotlin.time.Clock
import kotlinx.serialization.Serializable
import kotlinx.serialization.decodeFromString
import kotlinx.serialization.encodeToString
import kotlinx.serialization.json.Json

@Serializable
internal data class SavedAuthSession(
    val email: String = "",
    val response: VerifyOtpResponse = VerifyOtpResponse(),
    val savedAtEpochMs: Long = Clock.System.now().toEpochMilliseconds()
)

internal data class AuthContextSnapshot(
    val userId: String,
    val assignmentId: String,
    val roleCode: String,
    val schoolId: String,
    val schoolLabel: String
)

@OptIn(ExperimentalEncodingApi::class)
internal object AuthSessionStore {
    private const val SESSION_KEY = "${APP_STORAGE_PREFIX}auth_session"
    private const val CRYPTO_KEY = "iSchoolConnect-auth-v1"

    private val settingsOrNull: Settings? by lazy(LazyThreadSafetyMode.SYNCHRONIZED) {
        runCatching { Settings() }.getOrNull()
    }
    private val json = Json {
        ignoreUnknownKeys = true
        isLenient = true
        explicitNulls = false
    }

    fun getSession(): SavedAuthSession? {
        val encoded = settingsOrNull?.getString(SESSION_KEY, "").orEmpty()
        if (encoded.isBlank()) return null
        return runCatching {
            val raw = decrypt(encoded)
            json.decodeFromString(SavedAuthSession.serializer(), raw)
        }.getOrNull()
    }

    fun saveSession(email: String, response: VerifyOtpResponse) {
        val session = SavedAuthSession(
            email = email.trim(),
            response = response,
            savedAtEpochMs = Clock.System.now().toEpochMilliseconds()
        )
        settingsOrNull?.putString(
            SESSION_KEY,
            encrypt(json.encodeToString(SavedAuthSession.serializer(), session))
        )
    }

    fun clear() {
        settingsOrNull?.remove(SESSION_KEY)
    }

    fun primaryContext(): AuthContextSnapshot? {
        val session = getSession() ?: return null
        val profile = session.response.identityContextProfiles.firstOrNull() ?: return null
        val schoolId = profile.schoolId.orEmpty()
        return AuthContextSnapshot(
            userId = session.response.userId.ifBlank { session.email },
            assignmentId = profile.roleAssignmentId,
            roleCode = deriveRoleCode(profile),
            schoolId = schoolId,
            schoolLabel = profile.schoolName.orEmpty().ifBlank {
                if (schoolId.isBlank()) "Platform Context" else "School Context"
            }
        )
    }

    private fun deriveRoleCode(profile: IdentityContextProfile): String {
        return profile.resolveRoleCode()
    }

    private fun encrypt(value: String): String {
        if (value.isBlank()) return ""
        val input = value.encodeToByteArray()
        val key = CRYPTO_KEY.encodeToByteArray()
        val out = ByteArray(input.size)
        for (index in input.indices) {
            out[index] = (input[index].toInt() xor key[index % key.size].toInt()).toByte()
        }
        return Base64.Default.encode(out)
    }

    private fun decrypt(value: String): String {
        if (value.isBlank()) return ""
        return runCatching {
            val input = Base64.Default.decode(value)
            val key = CRYPTO_KEY.encodeToByteArray()
            val out = ByteArray(input.size)
            for (index in input.indices) {
                out[index] = (input[index].toInt() xor key[index % key.size].toInt()).toByte()
            }
            out.decodeToString()
        }.getOrDefault("")
    }
}
